import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSalon } from '../context/SalonContext';
import { 
  HomeIcon, 
  CalendarIcon, 
  SparklesIcon, 
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  UsersIcon,
  Squares2X2Icon,
  BuildingStorefrontIcon,
  ShieldCheckIcon,
  ScissorsIcon,
  BriefcaseIcon,
  BanknotesIcon,
  BellIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import NotificationCenter from './NotificationCenter';

const Sidebar = ({ role, isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mySalon } = useSalon();
  
  const currentRole = role || user?.role;

  let menuItems = [];

  if (currentRole === 'client') {
    menuItems = [
      { name: 'Accueil', path: '/', icon: HomeIcon },
      { name: 'Recherche', path: '/search', icon: ScissorsIcon },
      { name: 'Mes Rendez-vous', path: '/appointments', icon: CalendarIcon },
      { name: 'Profil', path: '/profile', icon: UserCircleIcon }
    ];
  } else if (currentRole === 'pro') {
    menuItems = [
      { name: 'Dashboard',         path: '/pro/dashboard',      icon: Squares2X2Icon },
      { name: 'Agenda',            path: '/pro/appointments',   icon: CalendarIcon },
      { name: 'Clients',           path: '/pro/clients',        icon: UsersIcon },
      { name: 'Employés',          path: '/pro/employees',      icon: BriefcaseIcon },
      { name: 'Services',          path: '/pro/services',       icon: ScissorsIcon },
      { name: 'Salon',             path: '/pro/salon',          icon: BuildingStorefrontIcon },
      { name: 'Avis clients',      path: '/pro/reviews',        icon: ChatBubbleBottomCenterTextIcon },
      { name: 'Notifications',     path: '/pro/notifications',  icon: BellIcon }
    ];
  } else if (currentRole === 'admin') {
    menuItems = [
      { name: 'Cockpit', path: '/admin/dashboard', icon: Squares2X2Icon },
      { name: 'Salons & Validation', path: '/admin/salons', icon: BuildingStorefrontIcon },
      { name: 'Utilisateurs', path: '/admin/users', icon: UsersIcon },
      { name: 'Réservations', path: '/admin/appointments', icon: CalendarIcon },
      { name: 'Paiements', path: '/admin/payments', icon: BanknotesIcon },
      { name: 'Signalements', path: '/admin/reports', icon: ShieldCheckIcon },
      { name: 'Marketing', path: '/admin/marketing', icon: SparklesIcon },
      { name: 'Audit Logs', path: '/admin/audit-logs', icon: ShieldCheckIcon }
    ];
  } else {
    // Default or guest
    menuItems = [{ name: 'Accueil', path: '/', icon: HomeIcon }];
  }

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <div className={`fixed inset-y-0 left-0 z-50 flex flex-col h-full bg-white border-r border-gray-100 w-64 lg:w-56 transition-transform duration-300 shadow-xl lg:shadow-[2px_0_8px_rgba(0,0,0,0.02)] lg:relative lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="p-5 border-b border-gray-50">
        <h1 className="text-xl font-bold text-gray-900 tracking-tighter flex items-center gap-1">
          Re-Glow<span className="text-reglow-primary">.</span>
        </h1>
      </div>

      {currentRole === 'pro' && mySalon && (
        <div className="px-5 mt-4 mb-2">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Mon Salon</label>
          <div className="bg-gray-50 border border-gray-200 text-gray-900 text-xs font-bold rounded-xl px-3 py-2.5 truncate">
            {mySalon.name}
          </div>
        </div>
      )}

      <nav className="flex-1 px-3 space-y-0.5 mt-3 overflow-y-auto">
        {currentRole !== 'pro' && (
          <div className="px-3 mb-2">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Menu</p>
          </div>
        )}
        {menuItems.map((item, index) => {
          if (item.type === 'header') {
            return (
              <div key={index} className="px-3 mt-5 mb-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{item.name}</p>
              </div>
            );
          }
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => {
                if (onClose) onClose();
              }}
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group relative ${
                isActive 
                  ? 'bg-primary-50/50 text-reglow-primary font-bold' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
              }`}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-reglow-primary rounded-r-full"></div>
              )}
              <item.icon className={`h-4 w-4 ${isActive ? 'text-reglow-primary' : 'text-gray-400 group-hover:text-gray-600'}`} strokeWidth={isActive ? 2 : 1.5} />
              <span className="text-[13px]">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-4 px-2">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="h-9 w-9 rounded-full bg-gray-100 flex-shrink-0 flex items-center justify-center text-gray-700 font-bold text-sm border border-gray-200">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="overflow-hidden pr-2">
              <p className="text-xs font-bold text-gray-900 truncate">{user?.name || 'Utilisateur'}</p>
              <p className="text-[10px] text-gray-500 truncate uppercase tracking-wider">{currentRole}</p>
            </div>
          </div>
          <div className="flex-shrink-0 text-gray-400">
            <NotificationCenter placement="bottom-left" />
          </div>
        </div>
        
        <button
          onClick={async () => {
            if (onClose) onClose();
            await logout();
            navigate('/login');
          }}
          className="flex items-center space-x-3 w-full px-3 py-2.5 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 font-semibold group"
        >
          <ArrowLeftOnRectangleIcon className="h-5 w-5 group-hover:text-red-500 text-gray-400" strokeWidth={1.5} />
          <span className="text-sm">Déconnexion</span>
        </button>

        <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-center gap-2.5 text-[11px] text-gray-400">
          <Link to="/privacy" className="hover:text-reglow-primary transition-colors">
            Confidentialité
          </Link>
          <span>•</span>
          <Link to="/terms" className="hover:text-reglow-primary transition-colors">
            Conditions
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;

